/* eslint-disable @typescript-eslint/no-explicit-any */
import { SlaEnum } from '@shared-private/models/candidature-analytics.model';
import { ChartConfiguration } from 'chart.js';

import { AssetStatistic } from './asset.model';
import { Candidature, CandidatureStats } from './candidature.model';
import { PolicyIssue } from './insurance.model';
import {
  RequestAnalysisByCandidature,
  RequestAnalysisDto
} from './users.model';

export class GeneralAnalytics {
  assetsDisabled: number;
  assetsOpen: number;
  assetsRented: number;
  candidaturesCreated: number;
  candidaturesReject: number;
  candidaturesSelect: number;
  date?: Date;
  homeownersActivated: number;
  homeownersCreated: number;
  id?: string;
  insurancesContracted: number;
  tenantsCreated: number;
}
export class Session {
  sessionId: string;
  paymentStatus: string;
  planCode: string;
  maxReportsMonth: number;
  maxCandidaturesMonth: number;
  initialPaymentDate: Date;
  expirationDate: Date;
  subscriptionId: string;
  maxReportsByAssetMonth: number;
  maxCandidaturesByAssetMonth: number;
  id?: string;
}
export class UserAnalytics {
  id?: string;
  date: Date;
  userId: string;
  userEmail: string;
  userName: string;
  userPhone?: string;
  bigTenant?: boolean;
  assetsOpen: number;
  assetsRented: number;
  assetsDisabled: number;
  candidaturesCreated: number;
  candidaturesSelect: number;
  candidaturesReject: number;
  requestedReports: number;
  insurancesContracted: number;
  registeredAmount: number;
  session?: Session;
}

export class AssetAnalytics {
  id: string;
  userId: string;
  street: string;
  number: string;
  portal: string;
  floor: string;
  door: string;
  postalCode: string;
  town: string;
  province: string;
  link: string;
  rentalPrice: number;
  assetStatus: string;
  creationDate: Date;
  rentedDate: Date;
  disabledDate: Date;
  visitedAsset: boolean;
  lastUpdated: Date;
  cadastralReference: string;
  rentalArea: number;
  labelId: string;
  candidatures: CandidatureAnalytics[];
}

export interface CandidatureAnalytics extends Candidature {
  requestAnalyses: RequestAnalysisDto[];
}

export class CandidatureAssetAnalytics {
  id: string;
  assetId: string;
  address: string;
  assetCreationDate: Date;
  price: number;
  status: string;
  invoiced: boolean;
  incidences: number;
  tenants: number;
  reportId: string;
  reportStartDate: Date;
  reportEndDate: Date;
  time: number;
  score: number;
}

export class AllDataOwnerAnalytics {
  insurancesData: PolicyIssue[];
  assetStatistic: AssetStatistic[];
  requestByCandidatureData: RequestAnalysisByCandidature[];
  activityCharts: {
    analysisByMonth: {
      counts: number[];
      data: ChartConfiguration<'bar'>['data'];
      options: ChartConfiguration<'bar'>['options'];
      showChart: boolean;
    };
    ocupationRate: {
      labels: string[];
      dataOpen: any[];
      dataRented: any[];
      data: ChartConfiguration<'bar'>['data'];
      options: ChartConfiguration<'bar'>['options'];
      showChart: boolean;
    };
  };
  timesCharts: {
    sla: {
      less12: number;
      between12and24: number;
      more24: number;
      data: ChartConfiguration<'pie'>['data'];
      options: ChartConfiguration<'pie'>['options'];
      showChart: boolean;
    };
    incidentAnalysis: {
      incidencePercentage: number;
      data: ChartConfiguration<'pie'>['data'];
      options: ChartConfiguration<'pie'>['options'];
      showChart: boolean;
    };
    tenantByCandidature: {
      candidaturesByTenantNumber: number[];
      backgroundColor: string[];
      hoverBackgroundColor: string[];
      data: ChartConfiguration<'bar'>['data'];
      options: ChartConfiguration<'bar'>['options'];
      showChart: boolean;
    };
    candidatureWithGuarantor: {
      candidaturesWithGuarantorData: number[];
      data: ChartConfiguration<'doughnut'>['data'];
      options: ChartConfiguration<'doughnut'>['options'];
      showChart: boolean;
    };
  };
  sinisterCharts: {
    sinestersByMonth: {
      totalPoliciesWithSinister: any[];
      totalPolicies: any[];
      data: ChartConfiguration<'bar'>['data'];
      options: ChartConfiguration<'bar'>['options'];
      showChart: boolean;
    };
    provinceRatio: {
      provinceRatio: any[];
      labels: string[];
      backgroundColor: string[];
      hoverBackgroundColor: string[];
      data: ChartConfiguration<'pie'>['data'];
      options: ChartConfiguration<'pie'>['options'];
      showChart: boolean;
    };
    priceRatio: {
      priceRatio: number[];
      backgroundColor: string[];
      hoverBackgroundColor: string[];
      data: ChartConfiguration<'pie'>['data'];
      options: ChartConfiguration<'pie'>['options'];
      showChart: boolean;
    };
  };
}

export class CandidatureInvoiceGroup {
  group: string;
  notes: string;
  isInvoiced: boolean;
  year: number;
  month: number;
  candidatures: CandidatureStats[];
}

export interface BaseNode {
  group: string;
  children?: BaseNode[];
  position?: string;
  status?: string;
}

export interface InvoiceGroupTreeNode extends BaseNode {
  group: string;
  notes: string;
  isInvoiced: boolean;
  year: number;
  month: number;
  children?: CandidatureNode[];
}

export interface CandidatureNode extends BaseNode {
  group: string;
  candidature: CandidatureStats;
}

export interface FlattenedInvoiceRow {
  Name?: string;
  InvoiceGroup?: string;
  candidatureId?: string;
  rentalPrice: number;
  rentalPriceGroup: string;
  incomes: number;
  withGuarantor: boolean;
  withInsuranceUnpaid: boolean;
  tenants: number;
  score: number;
  scoreGroup: string;
  effort: string;
  effortWithoutGuarantor: string;
  email: string;
  isInvoiced: boolean;
  year: number;
  month: number;
  deliveryDate: string;
  status: string;
  effortNumber: number;
  effortWithoutGuarantorNumber: number;
  requestDate: string;
  groupTimeOperative: string;
  startHour: number;
  endHour: number;
  incidents: number;
  incomesWithoutGuarantors: number;
  time: number;
  timeOperative: number;
  startWeekDay: string;
  endWeekDay: string;
  address: string;
  assetId: string;
  buildingId: string;
  candidatureStatusEnum: string;
  door: string;
  floor: string;
  homeownerUserEmail: string;
  homeownerUserId: string;
  homeownerReference: string;
  latitude: string;
  longitude: string;
  number: string;
  policyHolderDataCcc: string;
  policyHolderDataCity: string;
  policyHolderDataDocument: string;
  policyHolderDataSurname1: string;
  portal: string;
  portfolioId: string;
  postalCode: string;
  province: string;
  selectDate: string;
  street: string;
  tenantDniList: string;
  tenantEmailList: string;
  tenantPolicyNumber: string;
  trialVersion: boolean;
  userType: string;
}

export interface StatisticPoint {
  date: string;
  value: number;
}

export interface SlaStats {
  sla: SlaEnum;
  percentage: number;
}
